'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { DevsuLogoComplete } from '@/assets/DevsuLogoComplete';
import { useForm } from 'react-hook-form';
import { registerContact } from '@/actions/hubspot/registerContact';
import cookies from 'js-cookie';
import { emailRegex, isCorporateEmail } from '@/utils';

// Default HubSpot configuration
const DEFAULT_HUBSPOT_PORTAL_ID = '7119515';
const DEFAULT_HUBSPOT_FORM_ID = 'c54880e9-1a1d-4609-a81e-a1133f6b5d1c';

interface INewsletterForm {
  email: string;
}

enum FormStatus {
  DIRTY = 'DIRTY',
  SUBMITTED = 'SUBMITTED',
  FAILED = 'FAILED',
  WITH_ERRORS = 'WITH_ERRORS',
}

/**
 * Props for `FooterBase`.
 */
export type FooterBaseProps = SliceComponentProps<Content.FooterBaseSlice>;

/**
 * Component for "FooterBase" Slices.
 */
const FooterBase: FC<FooterBaseProps> = ({ slice }) => {
  // Handle both variations
  if (slice.variation === 'complexFooterBase') {
    return <ComplexFooterBase slice={slice} />;
  }

  return <SimpleFooterBase slice={slice} />;
};

/**
 * Simple Footer Base variation
 */
const SimpleFooterBase: FC<{ slice: Content.FooterBaseSliceSimple }> = ({
  slice,
}) => {
  const { logo_image, copyright_text } = slice.primary;

  const logoRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const copyrightRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const logoElement = logoRef.current;
    const dividerElement = dividerRef.current;
    const copyrightElement = copyrightRef.current;

    if (logoElement) {
      logoElement.style.transitionDelay = '0ms';
      observer.observe(logoElement);
    }
    if (dividerElement) {
      dividerElement.style.transitionDelay = '150ms';
      observer.observe(dividerElement);
    }
    if (copyrightElement) {
      copyrightElement.style.transitionDelay = '300ms';
      observer.observe(copyrightElement);
    }

    return () => {
      if (logoElement) {
        observer.unobserve(logoElement);
      }
      if (dividerElement) {
        observer.unobserve(dividerElement);
      }
      if (copyrightElement) {
        observer.unobserve(copyrightElement);
      }
    };
  }, []);

  return (
    <footer
      data-slice-type="footer_base"
      data-slice-variation={slice.variation}
      className="w-full pt-[60px] overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0E1110 0%, #0F3932 100%)',
      }}
    >
      <div className="flex flex-col items-center">
        {/* Logo */}
        {isFilled.image(logo_image) && (
          <div
            ref={logoRef}
            className="flex items-center justify-center mb-[80px] opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            <PrismicNextImage field={logo_image} className="h-[40px] w-auto" />
          </div>
        )}

        {/* Divider - full viewport width */}
        <div
          ref={dividerRef}
          className="w-screen border-t border-b border-[#666] opacity-0 translate-y-8 transition-all duration-700 ease-out"
        />

        {/* Copyright */}
        {copyright_text && (
          <p
            ref={copyrightRef}
            className="text-white/60 text-[14px] font-normal text-center pt-6 pb-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {copyright_text}
          </p>
        )}
      </div>
    </footer>
  );
};

/**
 * Complex Footer Base variation
 */
const ComplexFooterBase: FC<{
  slice: Content.FooterBaseSliceComplexFooterBase;
}> = ({ slice }) => {
  const {
    logo_image,
    social_media_icons,
    newsletter_description,
    logos,
    links,
    copyright_text,
  } = slice.primary;

  // Get newsletter fields with fallbacks
  const formId = (slice.primary as Record<string, unknown>)
    .newsletter_form_id as string | undefined;
  const buttonLabel =
    ((slice.primary as Record<string, unknown>).newsletter_button_label as
      | string
      | undefined) || 'Subscribe';
  const emailPlaceholder =
    ((slice.primary as Record<string, unknown>).newsletter_placeholder as
      | string
      | undefined) || 'Enter your email';

  const hubspotFormId = formId || DEFAULT_HUBSPOT_FORM_ID;

  const {
    register,
    handleSubmit,
    reset,
    formState: {
      errors: { email: emailError },
      isSubmitting,
    },
  } = useForm<INewsletterForm>({ mode: 'onBlur' });

  const [status, setStatus] = useState<FormStatus>(FormStatus.DIRTY);

  const formSubmit = async (values: INewsletterForm) => {
    reset();
    try {
      const { postSubmitAction } = await registerContact({
        properties: {
          email: values.email,
        },
        hubspotPortalIdForm: DEFAULT_HUBSPOT_PORTAL_ID,
        hubspotGuidForm: hubspotFormId,
        hubspotutkCookie: cookies.get('hubspotutk'),
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName:
          typeof document !== 'undefined'
            ? document.title
            : 'Footer Newsletter',
      });
      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.assign(postSubmitAction.value);
        return;
      }
      setStatus(FormStatus.SUBMITTED);
    } catch {
      setStatus(FormStatus.FAILED);
    }
  };

  return (
    <footer
      data-slice-type="footer_base"
      data-slice-variation={slice.variation}
      className="w-full py-[30px] px-4 md:px-[200px]"
      style={{
        background: 'linear-gradient(180deg, #0E1110 0%, #0F3932 100%)',
      }}
    >
      <div className="flex flex-col gap-[30px] items-center max-w-[1440px] mx-auto">
        {/* Logo */}
        <div className="flex items-center justify-center">
          {isFilled.image(logo_image) ? (
            <PrismicNextImage
              field={logo_image}
              className="h-[42px] w-auto"
              width={187}
              height={42}
            />
          ) : (
            <DevsuLogoComplete width={187} height={42} />
          )}
        </div>

        {/* Social Media Icons */}
        {social_media_icons && social_media_icons.length > 0 && (
          <div className="flex gap-4 items-center">
            {social_media_icons.map((item, index) => (
              <PrismicNextLink
                key={index}
                field={item.link}
                className="text-white hover:text-[#05F273] transition-colors duration-200 py-2"
              >
                {isFilled.image(item.icon) ? (
                  <PrismicNextImage
                    field={item.icon}
                    className="w-6 h-6"
                    width={24}
                    height={24}
                  />
                ) : (
                  <div className="w-6 h-6" />
                )}
              </PrismicNextLink>
            ))}
          </div>
        )}

        {/* Newsletter Section */}
        <div className="flex flex-col gap-3 items-center w-full max-w-[604px]">
          {newsletter_description && (
            <p className="font-manrope font-normal text-base leading-6 text-white text-center">
              {newsletter_description}
            </p>
          )}

          {/* Newsletter Form */}
          {status === FormStatus.SUBMITTED ? (
            <div className="text-[#05F273] text-base font-medium py-4">
              Thank you for subscribing!
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(formSubmit)}
              className="flex w-full max-w-[604px]"
            >
              <input
                type="email"
                placeholder={emailPlaceholder}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: emailRegex,
                    message: 'Please enter a valid email',
                  },
                  validate: {
                    isCorporateEmail: (value) =>
                      isCorporateEmail(value) || 'Please use a corporate email',
                  },
                })}
                className={`flex-1 border ${
                  emailError ? 'border-red-400' : 'border-white'
                } rounded-l-lg p-3 bg-transparent text-white placeholder:text-white/60 font-inter text-base focus:outline-none focus:border-[#05F273]`}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#05F273] border-[1.5px] border-[#05F273] rounded-r-lg px-6 py-3 font-manrope font-bold text-base text-black hover:bg-[#04d966] transition-colors duration-200 disabled:opacity-50"
              >
                {isSubmitting ? 'Subscribing...' : buttonLabel}
              </button>
            </form>
          )}

          {/* Error messages */}
          {emailError && (
            <p className="text-red-400 text-sm">{emailError.message}</p>
          )}
          {status === FormStatus.FAILED && (
            <p className="text-red-400 text-sm">
              Something went wrong. Please try again.
            </p>
          )}

          {/* Privacy notice */}
          <p className="font-manrope font-normal text-xs text-white text-center">
            By subscribing you agree to with our{' '}
            <span className="underline cursor-pointer hover:text-[#05F273]">
              Privacy Policy
            </span>{' '}
            and provide consent to receive updates from our company.
          </p>
        </div>

        {/* Partner/Certification Logos - Single row aligned with form */}
        {logos && logos.length > 0 && (
          <div className="flex items-center justify-between w-full max-w-[604px]">
            {logos.map((item, index) => {
              // Different sizes for each logo position based on Figma design
              const logoSizeClasses = [
                'h-[52px] w-[48px]', // Clutch badge
                'h-[26px] w-[75px]', // Star rating badge
                'h-[60px] w-[60px]', // Certification badge
                'h-[22px] w-[94px]', // GoodFirms
                'h-[50px] w-[50px]', // Circle badge 1
                'h-[50px] w-[50px]', // Circle badge 2
              ];
              const sizeClass = logoSizeClasses[index] || 'h-[50px] w-auto';

              return (
                <div
                  key={index}
                  className="flex items-center justify-center mix-blend-luminosity hover:mix-blend-normal transition-all duration-300"
                >
                  {isFilled.image(item.logo) && (
                    <PrismicNextImage
                      field={item.logo}
                      className={`${sizeClass} object-contain`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer Links */}
        {links && links.length > 0 && (
          <div className="flex flex-wrap gap-6 items-center justify-center">
            {links.map((item, index) => (
              <PrismicNextLink
                key={index}
                field={item.link}
                className="font-inter font-normal text-sm text-[#ccc] underline hover:text-[#05F273] transition-colors duration-200"
              >
                {item.link && 'text' in item.link ? item.link.text : 'Link'}
              </PrismicNextLink>
            ))}
          </div>
        )}

        {/* Copyright */}
        {copyright_text && (
          <p className="text-white/60 text-sm font-normal text-center">
            {copyright_text}
          </p>
        )}
      </div>
    </footer>
  );
};

export default FooterBase;
